import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { createChart, CrosshairMode, ColorType } from 'lightweight-charts';
import { Translate, I18n } from 'react-redux-i18n';
import {
    FaMousePointer,
    FaMinus,
    FaChartLine,
    FaStickyNote,
    FaTrash,
} from 'react-icons/fa';
import { TIMEFRAMES, buildMockCandles, summarizeCandles } from 'utils/mockStockCandles';
import useChartDrawings from 'hooks/useChartDrawings';
import './TradingViewChart.css';

const TOOL_CURSOR = 'cursor';
const TOOL_HLINE = 'hline';
const TOOL_TREND = 'trend';
const TOOL_NOTE = 'note';

const DRAW_COLORS = {
    hline: '#f59e0b',
    trend: '#a78bfa',
    note: '#38bdf8',
};

const MA_PERIODS = [
    { period: 10, color: '#fbbf24', key: 10 },
    { period: 50, color: '#38bdf8', key: 50 },
    { period: 100, color: '#a78bfa', key: 100 },
    { period: 200, color: '#f472b6', key: 200 },
];

function computeSMA(ohlc, period) {
    const out = [];
    if (!Array.isArray(ohlc) || period < 1) {
        return out;
    }
    let sum = 0;
    for (let i = 0; i < ohlc.length; i += 1) {
        sum += Number(ohlc[i].close);
        if (i >= period) {
            sum -= Number(ohlc[i - period].close);
        }
        if (i >= period - 1) {
            out.push({
                time: ohlc[i].time,
                value: sum / period,
            });
        }
    }
    return out;
}

function toUnix(dateValue) {
    const ms = new Date(dateValue).getTime();
    if (!Number.isFinite(ms)) {
        return null;
    }
    return Math.floor(ms / 1000);
}

function formatPrice(value, digits = 2) {
    if (!Number.isFinite(value)) {
        return '—';
    }
    return value.toLocaleString(undefined, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    });
}

function formatVolume(value) {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) {
        return '—';
    }
    if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
    if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
    if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
    return String(Math.round(n));
}

function formatCrosshairTime(time, timeframe) {
    if (time == null) {
        return '';
    }
    let date;
    if (typeof time === 'number') {
        date = new Date(time * 1000);
    } else if (typeof time === 'object' && time.year && time.month && time.day) {
        date = new Date(Date.UTC(time.year, time.month - 1, time.day));
    } else {
        date = new Date(time);
    }
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    if (timeframe === '1d' || timeframe === '1w') {
        return date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC',
        });
    }
    return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function toChartTime(dateValue, timeframe) {
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) {
        return null;
    }
    if (timeframe === '1d' || timeframe === '1w') {
        return {
            year: date.getUTCFullYear(),
            month: date.getUTCMonth() + 1,
            day: date.getUTCDate(),
        };
    }
    return Math.floor(date.getTime() / 1000);
}

function mapCandles(candles, timeframe) {
    const ohlc = [];
    const volume = [];
    const seen = new Set();
    (candles || []).forEach((candle) => {
        const time = toChartTime(candle.date, timeframe);
        if (time == null) {
            return;
        }
        const key = typeof time === 'number'
            ? String(time)
            : `${time.year}-${time.month}-${time.day}`;
        if (seen.has(key)) {
            return;
        }
        seen.add(key);
        const open = Number(candle.open);
        const high = Number(candle.high);
        const low = Number(candle.low);
        const close = Number(candle.close);
        if (![open, high, low, close].every(Number.isFinite)) {
            return;
        }
        ohlc.push({ time, open, high, low, close });
        volume.push({
            time,
            value: Number(candle.volume) || 0,
            color: close >= open
                ? 'rgba(38, 166, 154, 0.5)'
                : 'rgba(239, 83, 80, 0.5)',
        });
    });
    return { ohlc, volume };
}

function normalizeChartTime(time) {
    if (time == null) return null;
    if (typeof time === 'number' && Number.isFinite(time)) return time;
    if (typeof time === 'object' && time.year && time.month && time.day) {
        return {
            year: Number(time.year),
            month: Number(time.month),
            day: Number(time.day),
        };
    }
    if (typeof time === 'string') {
        const d = new Date(time);
        if (!Number.isNaN(d.getTime())) {
            return Math.floor(d.getTime() / 1000);
        }
    }
    return null;
}

function distPointToSegment(px, py, x1, y1, x2, y2) {
    const dx = x2 - x1;
    const dy = y2 - y1;
    if (dx === 0 && dy === 0) {
        return Math.hypot(px - x1, py - y1);
    }
    const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / (dx * dx + dy * dy)));
    return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}

/**
 * TradingView-style candlestick chart powered by lightweight-charts.
 * @see https://www.tradingview.com/lightweight-charts/
 */
const TradingViewChart = ({
    symbol,
    basePrice,
    entryPrice,
    candles: candlesProp,
    loading,
    error,
    timeframe: timeframeProp,
    onTimeframeChange,
    timeframes,
    height,
    drawingsEnabled,
    userId,
}) => {
    const wrapRef = useRef(null);
    const overlayRef = useRef(null);
    const chartRef = useRef(null);
    const candleSeriesRef = useRef(null);
    const volumeSeriesRef = useRef(null);
    const maSeriesRef = useRef(new Map());
    const entryLineRef = useRef(null);
    const hlineRefs = useRef(new Map());
    const drawingsRef = useRef([]);
    const toolRef = useRef(TOOL_CURSOR);
    const pendingTrendRef = useRef(null);
    const selectedIdRef = useRef(null);
    const paintRef = useRef(() => {});
    const clickHandlerRef = useRef(null);

    const [internalTimeframe, setInternalTimeframe] = useState('1d');
    const [hover, setHover] = useState(null);
    const [tool, setTool] = useState(TOOL_CURSOR);
    const [selectedId, setSelectedId] = useState(null);
    const [pendingTrend, setPendingTrend] = useState(null);
    const [drawBusy, setDrawBusy] = useState(false);
    const [maEnabled, setMaEnabled] = useState({
        10: true,
        50: true,
        100: true,
        200: true,
    });
    const [maHover, setMaHover] = useState(null);

    const isControlled = Boolean(onTimeframeChange);
    const timeframe = isControlled ? timeframeProp : internalTimeframe;
    const isMock = !Array.isArray(candlesProp);
    const tabs = timeframes && timeframes.length ? timeframes : TIMEFRAMES;
    const canDraw = Boolean(drawingsEnabled && userId);

    const {
        drawings,
        createDrawing,
        deleteDrawing,
    } = useChartDrawings(symbol, userId, canDraw);

    const candles = useMemo(() => {
        if (!isMock) {
            return candlesProp || [];
        }
        return buildMockCandles({ symbol, basePrice, timeframe });
    }, [isMock, candlesProp, symbol, basePrice, timeframe]);

    const summary = useMemo(() => summarizeCandles(candles), [candles]);
    const seriesData = useMemo(
        () => mapCandles(candles, timeframe),
        [candles, timeframe],
    );

    const maSeriesData = useMemo(() => {
        const map = {};
        MA_PERIODS.forEach(({ period }) => {
            map[period] = computeSMA(seriesData.ohlc, period);
        });
        return map;
    }, [seriesData]);

    drawingsRef.current = drawings;
    toolRef.current = tool;
    pendingTrendRef.current = pendingTrend;
    selectedIdRef.current = selectedId;

    const setTimeframe = (id) => {
        if (isControlled) {
            onTimeframeChange(id);
            return;
        }
        setInternalTimeframe(id);
    };

    const paintOverlay = useCallback(() => {
        const canvas = overlayRef.current;
        const chart = chartRef.current;
        const series = candleSeriesRef.current;
        if (!canvas || !chart || !series) {
            return;
        }

        const rect = canvas.getBoundingClientRect();
        const w = rect.width;
        const h = rect.height;
        if (w <= 0 || h <= 0) {
            return;
        }

        const dpr = window.devicePixelRatio || 1;
        canvas.width = Math.floor(w * dpr);
        canvas.height = Math.floor(h * dpr);
        const ctx = canvas.getContext('2d');
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, w, h);

        const timeScale = chart.timeScale();
        const selected = selectedIdRef.current;
        const pending = pendingTrendRef.current;

        drawingsRef.current.forEach((drawing) => {
            const payload = drawing.payload || {};
            const isSelected = drawing.id === selected;
            const color = payload.color
                || DRAW_COLORS[drawing.type]
                || '#94a3b8';

            if (drawing.type === TOOL_TREND) {
                const t1 = normalizeChartTime(payload.t1);
                const t2 = normalizeChartTime(payload.t2);
                const x1 = t1 != null ? timeScale.timeToCoordinate(t1) : null;
                const x2 = t2 != null ? timeScale.timeToCoordinate(t2) : null;
                const y1 = series.priceToCoordinate(Number(payload.p1));
                const y2 = series.priceToCoordinate(Number(payload.p2));
                if (x1 == null || x2 == null || y1 == null || y2 == null) {
                    return;
                }
                ctx.beginPath();
                ctx.moveTo(x1, y1);
                ctx.lineTo(x2, y2);
                ctx.strokeStyle = color;
                ctx.lineWidth = isSelected ? 2.5 : 1.5;
                ctx.setLineDash(isSelected ? [6, 4] : []);
                ctx.stroke();
                ctx.setLineDash([]);
                ctx.fillStyle = color;
                ctx.beginPath();
                ctx.arc(x1, y1, isSelected ? 4 : 3, 0, Math.PI * 2);
                ctx.arc(x2, y2, isSelected ? 4 : 3, 0, Math.PI * 2);
                ctx.fill();
                return;
            }

            if (drawing.type === TOOL_NOTE) {
                const t = normalizeChartTime(payload.t);
                const x = t != null ? timeScale.timeToCoordinate(t) : null;
                const y = series.priceToCoordinate(Number(payload.p));
                if (x == null || y == null) {
                    return;
                }
                ctx.fillStyle = color;
                ctx.beginPath();
                ctx.arc(x, y, isSelected ? 6 : 5, 0, Math.PI * 2);
                ctx.fill();
                ctx.fillStyle = '#0f172a';
                ctx.font = 'bold 10px sans-serif';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText('N', x, y);

                const text = String(payload.text || '').slice(0, 40);
                if (text) {
                    ctx.font = '11px sans-serif';
                    ctx.textAlign = 'left';
                    ctx.textBaseline = 'bottom';
                    const pad = 4;
                    const metrics = ctx.measureText(text);
                    const tw = metrics.width + pad * 2;
                    const th = 16;
                    const bx = x + 8;
                    const by = y - 8;
                    ctx.fillStyle = 'rgba(15, 23, 42, 0.85)';
                    ctx.strokeStyle = color;
                    ctx.lineWidth = 1;
                    ctx.beginPath();
                    ctx.roundRect
                        ? ctx.roundRect(bx, by - th, tw, th, 4)
                        : ctx.rect(bx, by - th, tw, th);
                    ctx.fill();
                    ctx.stroke();
                    ctx.fillStyle = '#e2e8f0';
                    ctx.fillText(text, bx + pad, by - 4);
                }
            }
        });

        if (pending?.t1 != null && pending?.preview) {
            const t1 = normalizeChartTime(pending.t1);
            const x1 = t1 != null ? timeScale.timeToCoordinate(t1) : null;
            const y1 = series.priceToCoordinate(Number(pending.p1));
            const x2 = pending.preview.x;
            const y2 = pending.preview.y;
            if (x1 != null && y1 != null && x2 != null && y2 != null) {
                ctx.beginPath();
                ctx.moveTo(x1, y1);
                ctx.lineTo(x2, y2);
                ctx.strokeStyle = DRAW_COLORS.trend;
                ctx.lineWidth = 1.5;
                ctx.setLineDash([4, 4]);
                ctx.stroke();
                ctx.setLineDash([]);
            }
        }
    }, []);

    paintRef.current = paintOverlay;

    const syncHlines = useCallback(() => {
        const series = candleSeriesRef.current;
        if (!series) {
            return;
        }

        const nextIds = new Set();
        drawingsRef.current.forEach((drawing) => {
            if (drawing.type !== TOOL_HLINE) {
                return;
            }
            nextIds.add(drawing.id);
            const price = Number(drawing.payload?.price);
            if (!Number.isFinite(price)) {
                return;
            }
            const color = drawing.payload?.color || DRAW_COLORS.hline;
            const title = drawing.payload?.title || '';
            const existing = hlineRefs.current.get(drawing.id);
            if (existing) {
                existing.applyOptions({
                    price,
                    color,
                    title,
                    lineWidth: drawing.id === selectedIdRef.current ? 2 : 1,
                });
                return;
            }
            const line = series.createPriceLine({
                price,
                color,
                lineWidth: drawing.id === selectedIdRef.current ? 2 : 1,
                lineStyle: 0,
                axisLabelVisible: true,
                title,
            });
            hlineRefs.current.set(drawing.id, line);
        });

        hlineRefs.current.forEach((line, id) => {
            if (!nextIds.has(id)) {
                try {
                    series.removePriceLine(line);
                } catch (e) {
                    // ignore
                }
                hlineRefs.current.delete(id);
            }
        });
    }, []);

    const findDrawingAt = useCallback((x, y) => {
        const chart = chartRef.current;
        const series = candleSeriesRef.current;
        if (!chart || !series) {
            return null;
        }
        const timeScale = chart.timeScale();
        let best = null;
        let bestDist = 12;

        drawingsRef.current.forEach((drawing) => {
            const payload = drawing.payload || {};
            if (drawing.type === TOOL_HLINE) {
                const py = series.priceToCoordinate(Number(payload.price));
                if (py == null) return;
                const dist = Math.abs(py - y);
                if (dist < bestDist) {
                    bestDist = dist;
                    best = drawing;
                }
                return;
            }
            if (drawing.type === TOOL_NOTE) {
                const t = normalizeChartTime(payload.t);
                const nx = t != null ? timeScale.timeToCoordinate(t) : null;
                const ny = series.priceToCoordinate(Number(payload.p));
                if (nx == null || ny == null) return;
                const dist = Math.hypot(nx - x, ny - y);
                if (dist < bestDist) {
                    bestDist = dist;
                    best = drawing;
                }
                return;
            }
            if (drawing.type === TOOL_TREND) {
                const t1 = normalizeChartTime(payload.t1);
                const t2 = normalizeChartTime(payload.t2);
                const x1 = t1 != null ? timeScale.timeToCoordinate(t1) : null;
                const x2 = t2 != null ? timeScale.timeToCoordinate(t2) : null;
                const y1 = series.priceToCoordinate(Number(payload.p1));
                const y2 = series.priceToCoordinate(Number(payload.p2));
                if (x1 == null || x2 == null || y1 == null || y2 == null) return;
                const dist = distPointToSegment(x, y, x1, y1, x2, y2);
                if (dist < bestDist) {
                    bestDist = dist;
                    best = drawing;
                }
            }
        });

        return best;
    }, []);

    useEffect(() => {
        const el = wrapRef.current;
        if (!el) {
            return undefined;
        }

        const chart = createChart(el, {
            autoSize: true,
            layout: {
                background: { type: ColorType.Solid, color: '#131722' },
                textColor: '#d1d4dc',
                fontSize: 12,
                fontFamily: "-apple-system, BlinkMacSystemFont, 'Trebuchet MS', Roboto, Ubuntu, sans-serif",
            },
            grid: {
                vertLines: { color: 'rgba(42, 46, 57, 0.6)' },
                horzLines: { color: 'rgba(42, 46, 57, 0.6)' },
            },
            crosshair: {
                mode: CrosshairMode.Normal,
                vertLine: {
                    color: 'rgba(224, 227, 235, 0.35)',
                    width: 1,
                    style: 2,
                    labelBackgroundColor: '#363a45',
                },
                horzLine: {
                    color: 'rgba(224, 227, 235, 0.35)',
                    width: 1,
                    style: 2,
                    labelBackgroundColor: '#363a45',
                },
            },
            rightPriceScale: {
                borderColor: 'rgba(42, 46, 57, 0.9)',
                scaleMargins: { top: 0.08, bottom: 0.22 },
            },
            timeScale: {
                borderColor: 'rgba(42, 46, 57, 0.9)',
                timeVisible: timeframe === '10m' || timeframe === '1h',
                secondsVisible: false,
                rightOffset: 4,
                barSpacing: 8,
            },
            handleScroll: {
                mouseWheel: true,
                pressedMouseMove: true,
                horzTouchDrag: true,
                vertTouchDrag: true,
            },
            handleScale: {
                axisPressedMouseMove: true,
                mouseWheel: true,
                pinch: true,
            },
        });

        const candleSeries = chart.addCandlestickSeries({
            upColor: '#26a69a',
            downColor: '#ef5350',
            borderUpColor: '#26a69a',
            borderDownColor: '#ef5350',
            wickUpColor: '#26a69a',
            wickDownColor: '#ef5350',
        });

        const volumeSeries = chart.addHistogramSeries({
            priceFormat: { type: 'volume' },
            priceScaleId: 'volume',
        });
        chart.priceScale('volume').applyOptions({
            scaleMargins: { top: 0.8, bottom: 0 },
        });

        const maMap = new Map();
        MA_PERIODS.forEach(({ period, color }) => {
            const series = chart.addLineSeries({
                color,
                lineWidth: 2,
                priceLineVisible: false,
                lastValueVisible: true,
                crosshairMarkerVisible: false,
                title: `MA${period}`,
            });
            maMap.set(period, series);
        });
        maSeriesRef.current = maMap;

        chartRef.current = chart;
        candleSeriesRef.current = candleSeries;
        volumeSeriesRef.current = volumeSeries;

        chart.subscribeCrosshairMove((param) => {
            if (!param || !param.time || !param.seriesData) {
                setHover(null);
                setMaHover(null);
            } else {
                const bar = param.seriesData.get(candleSeries);
                const vol = param.seriesData.get(volumeSeries);
                if (!bar) {
                    setHover(null);
                    setMaHover(null);
                } else {
                    setHover({
                        time: param.time,
                        open: bar.open,
                        high: bar.high,
                        low: bar.low,
                        close: bar.close,
                        volume: vol?.value,
                    });
                    const maValues = {};
                    maMap.forEach((series, period) => {
                        const point = param.seriesData.get(series);
                        if (point && Number.isFinite(point.value)) {
                            maValues[period] = point.value;
                        }
                    });
                    setMaHover(Object.keys(maValues).length ? maValues : null);
                }
            }

            if (toolRef.current === TOOL_TREND && pendingTrendRef.current && param?.point) {
                setPendingTrend((prev) => (
                    prev
                        ? { ...prev, preview: { x: param.point.x, y: param.point.y } }
                        : prev
                ));
            }
        });

        chart.subscribeClick((param) => {
            if (!param?.point) {
                return;
            }
            const handler = clickHandlerRef.current;
            if (handler) {
                handler(param);
            }
        });

        const onVisible = () => {
            paintRef.current();
        };
        chart.timeScale().subscribeVisibleLogicalRangeChange(onVisible);
        chart.timeScale().subscribeVisibleTimeRangeChange(onVisible);

        return () => {
            hlineRefs.current.forEach((line) => {
                try {
                    candleSeries.removePriceLine(line);
                } catch (e) {
                    // ignore
                }
            });
            hlineRefs.current.clear();
            maSeriesRef.current.clear();
            chart.remove();
            chartRef.current = null;
            candleSeriesRef.current = null;
            volumeSeriesRef.current = null;
        };
    }, []);

    useEffect(() => {
        const chart = chartRef.current;
        if (!chart) {
            return;
        }
        chart.applyOptions({
            timeScale: {
                timeVisible: timeframe === '10m' || timeframe === '1h',
            },
        });
    }, [timeframe]);

    useEffect(() => {
        const chart = chartRef.current;
        if (!chart) {
            return;
        }
        const drawing = tool !== TOOL_CURSOR;
        chart.applyOptions({
            handleScroll: drawing
                ? false
                : {
                    mouseWheel: true,
                    pressedMouseMove: true,
                    horzTouchDrag: true,
                    vertTouchDrag: true,
                },
            handleScale: drawing
                ? false
                : {
                    axisPressedMouseMove: true,
                    mouseWheel: true,
                    pinch: true,
                },
        });
    }, [tool]);

    useEffect(() => {
        const candleSeries = candleSeriesRef.current;
        const volumeSeries = volumeSeriesRef.current;
        const chart = chartRef.current;
        if (!candleSeries || !volumeSeries || !chart) {
            return;
        }

        candleSeries.setData(seriesData.ohlc);
        volumeSeries.setData(seriesData.volume);

        maSeriesRef.current.forEach((series, period) => {
            const enabled = Boolean(maEnabled[period]);
            series.applyOptions({
                visible: enabled,
                lastValueVisible: enabled,
            });
            series.setData(enabled ? (maSeriesData[period] || []) : []);
        });

        if (entryLineRef.current) {
            try {
                candleSeries.removePriceLine(entryLineRef.current);
            } catch (e) {
                // series may have been recreated
            }
            entryLineRef.current = null;
        }

        if (Number.isFinite(entryPrice) && entryPrice > 0) {
            entryLineRef.current = candleSeries.createPriceLine({
                price: entryPrice,
                color: '#38bdf8',
                lineWidth: 1,
                lineStyle: 2,
                axisLabelVisible: true,
                title: 'Entry',
            });
        }

        if (seriesData.ohlc.length) {
            chart.timeScale().fitContent();
        }
        setHover(null);
        setMaHover(null);
        syncHlines();
        paintOverlay();
    }, [seriesData, entryPrice, syncHlines, paintOverlay]);

    useEffect(() => {
        maSeriesRef.current.forEach((series, period) => {
            const enabled = Boolean(maEnabled[period]);
            series.applyOptions({
                visible: enabled,
                lastValueVisible: enabled,
            });
            series.setData(enabled ? (maSeriesData[period] || []) : []);
        });
    }, [maEnabled, maSeriesData]);

    useEffect(() => {
        syncHlines();
        paintOverlay();
    }, [drawings, selectedId, pendingTrend, syncHlines, paintOverlay]);

    const resolveClickPoint = useCallback((param) => {
        const series = candleSeriesRef.current;
        if (!series || !param?.point) {
            return null;
        }
        const { x, y } = param.point;
        const time = param.time != null
            ? param.time
            : chartRef.current?.timeScale().coordinateToTime(x);
        const price = series.coordinateToPrice(y);
        if (time == null || !Number.isFinite(price)) {
            return null;
        }
        return { x, y, time, price };
    }, []);

    useEffect(() => {
        clickHandlerRef.current = async (param) => {
            if (!canDraw || drawBusy) {
                return;
            }
            const point = resolveClickPoint(param);
            if (!point) {
                return;
            }

            const currentTool = toolRef.current;

            if (currentTool === TOOL_CURSOR) {
                const hit = findDrawingAt(point.x, point.y);
                setSelectedId(hit ? hit.id : null);
                return;
            }

            if (currentTool === TOOL_HLINE) {
                setDrawBusy(true);
                try {
                    const item = await createDrawing(TOOL_HLINE, {
                        price: Number(point.price),
                        color: DRAW_COLORS.hline,
                    });
                    if (item) {
                        setSelectedId(item.id);
                    }
                } catch (e) {
                    // ignore
                } finally {
                    setDrawBusy(false);
                    setTool(TOOL_CURSOR);
                }
                return;
            }

            if (currentTool === TOOL_TREND) {
                if (!pendingTrendRef.current) {
                    setPendingTrend({ t1: point.time, p1: point.price, preview: null });
                    return;
                }
                const first = pendingTrendRef.current;
                setDrawBusy(true);
                try {
                    const item = await createDrawing(TOOL_TREND, {
                        t1: first.t1,
                        p1: Number(first.p1),
                        t2: point.time,
                        p2: Number(point.price),
                        color: DRAW_COLORS.trend,
                    });
                    if (item) {
                        setSelectedId(item.id);
                    }
                } catch (e) {
                    // ignore
                } finally {
                    setPendingTrend(null);
                    setDrawBusy(false);
                    setTool(TOOL_CURSOR);
                }
                return;
            }

            if (currentTool === TOOL_NOTE) {
                const text = window.prompt(I18n.t('chart_draw_note_prompt'), '');
                if (text == null) {
                    setTool(TOOL_CURSOR);
                    return;
                }
                const trimmed = String(text).trim().slice(0, 280);
                if (!trimmed) {
                    setTool(TOOL_CURSOR);
                    return;
                }
                setDrawBusy(true);
                try {
                    const item = await createDrawing(TOOL_NOTE, {
                        t: point.time,
                        p: Number(point.price),
                        text: trimmed,
                        color: DRAW_COLORS.note,
                    });
                    if (item) {
                        setSelectedId(item.id);
                    }
                } catch (e) {
                    // ignore
                } finally {
                    setDrawBusy(false);
                    setTool(TOOL_CURSOR);
                }
            }
        };
    }, [canDraw, drawBusy, createDrawing, findDrawingAt, resolveClickPoint]);

    const handleDeleteSelected = async () => {
        if (!selectedId) {
            return;
        }
        const id = selectedId;
        setSelectedId(null);
        try {
            await deleteDrawing(id);
        } catch (e) {
            // ignore
        }
    };

    const handleToolChange = (next) => {
        setPendingTrend(null);
        setTool(next);
        if (next !== TOOL_CURSOR) {
            setSelectedId(null);
        }
    };

    const toggleMa = (period) => {
        setMaEnabled((prev) => ({
            ...prev,
            [period]: !prev[period],
        }));
    };

    const active = hover || (candles.length
        ? {
            open: candles[candles.length - 1].open,
            high: candles[candles.length - 1].high,
            low: candles[candles.length - 1].low,
            close: candles[candles.length - 1].close,
            volume: candles[candles.length - 1].volume,
            time: toUnix(candles[candles.length - 1].date),
        }
        : null);

    const change = active
        ? Number(active.close) - Number(active.open)
        : summary.change;
    const changePct = active && Number(active.open)
        ? (change / Number(active.open)) * 100
        : summary.changePct;
    const isUp = change >= 0;

    return (
        <div className={`tv-chart${canDraw ? ' tv-chart--draw' : ''}`}>
            <div className="tv-chart-toolbar">
                <div className="tv-chart-symbol-block">
                    <span className="tv-chart-symbol">{symbol}</span>
                    {isMock && (
                        <span className="tv-chart-mock">
                            <Translate value="analysis_stock_mock_badge" />
                        </span>
                    )}
                </div>
                <div className="tv-chart-tf" role="tablist" aria-label="Timeframe">
                    {tabs.map((tf) => (
                        <button
                            key={tf.id}
                            type="button"
                            role="tab"
                            aria-selected={timeframe === tf.id}
                            className={`tv-chart-tf-btn${timeframe === tf.id ? ' is-active' : ''}`}
                            onClick={() => setTimeframe(tf.id)}
                        >
                            <Translate value={tf.labelKey} />
                        </button>
                    ))}
                </div>
                <div className="tv-chart-ma" role="group" aria-label={I18n.t('chart_ma_group')}>
                    {MA_PERIODS.map(({ period, color }) => (
                        <button
                            key={period}
                            type="button"
                            className={`tv-chart-ma-btn${maEnabled[period] ? ' is-active' : ''}`}
                            style={{ '--ma-color': color }}
                            title={`MA ${period}`}
                            aria-pressed={maEnabled[period]}
                            onClick={() => toggleMa(period)}
                        >
                            MA{period}
                        </button>
                    ))}
                </div>
            </div>

            <div className="tv-chart-legend">
                {active ? (
                    <>
                        <span className="tv-chart-legend-time">
                            {formatCrosshairTime(active.time, timeframe)}
                        </span>
                        <span>
                            O <b>{formatPrice(Number(active.open))}</b>
                        </span>
                        <span>
                            H <b>{formatPrice(Number(active.high))}</b>
                        </span>
                        <span>
                            L <b>{formatPrice(Number(active.low))}</b>
                        </span>
                        <span>
                            C <b className={isUp ? 'is-up' : 'is-down'}>
                                {formatPrice(Number(active.close))}
                            </b>
                        </span>
                        <span className={isUp ? 'is-up' : 'is-down'}>
                            {isUp ? '+' : ''}
                            {formatPrice(change)}
                            {' '}
                            ({isUp ? '+' : ''}
                            {Number(changePct).toFixed(2)}%)
                        </span>
                        <span>
                            Vol <b>{formatVolume(active.volume)}</b>
                        </span>
                        {MA_PERIODS.map(({ period, color }) => {
                            if (!maEnabled[period]) {
                                return null;
                            }
                            const value = maHover?.[period];
                            if (!Number.isFinite(value)) {
                                return null;
                            }
                            return (
                                <span key={period} className="tv-chart-legend-ma" style={{ color }}>
                                    MA{period} <b>{formatPrice(value)}</b>
                                </span>
                            );
                        })}
                    </>
                ) : (
                    <span className="tv-chart-legend-empty">
                        <Translate value="analysis_chart_empty" />
                    </span>
                )}
            </div>

            <div className="tv-chart-stage">
                {canDraw && (
                    <div className="tv-chart-draw-tools" role="toolbar" aria-label={I18n.t('chart_draw_tools')}>
                        <button
                            type="button"
                            className={`tv-chart-draw-btn${tool === TOOL_CURSOR ? ' is-active' : ''}`}
                            title={I18n.t('chart_draw_cursor')}
                            aria-label={I18n.t('chart_draw_cursor')}
                            onClick={() => handleToolChange(TOOL_CURSOR)}
                        >
                            <FaMousePointer size={16} aria-hidden />
                        </button>
                        <button
                            type="button"
                            className={`tv-chart-draw-btn${tool === TOOL_HLINE ? ' is-active' : ''}`}
                            title={I18n.t('chart_draw_hline')}
                            aria-label={I18n.t('chart_draw_hline')}
                            onClick={() => handleToolChange(TOOL_HLINE)}
                        >
                            <FaMinus size={16} aria-hidden />
                        </button>
                        <button
                            type="button"
                            className={`tv-chart-draw-btn${tool === TOOL_TREND ? ' is-active' : ''}`}
                            title={I18n.t('chart_draw_trend')}
                            aria-label={I18n.t('chart_draw_trend')}
                            onClick={() => handleToolChange(TOOL_TREND)}
                        >
                            <FaChartLine size={16} aria-hidden />
                        </button>
                        <button
                            type="button"
                            className={`tv-chart-draw-btn${tool === TOOL_NOTE ? ' is-active' : ''}`}
                            title={I18n.t('chart_draw_note')}
                            aria-label={I18n.t('chart_draw_note')}
                            onClick={() => handleToolChange(TOOL_NOTE)}
                        >
                            <FaStickyNote size={16} aria-hidden />
                        </button>
                        <button
                            type="button"
                            className="tv-chart-draw-btn tv-chart-draw-btn--danger"
                            title={I18n.t('chart_draw_delete')}
                            aria-label={I18n.t('chart_draw_delete')}
                            disabled={!selectedId || drawBusy}
                            onClick={handleDeleteSelected}
                        >
                            <FaTrash size={15} aria-hidden />
                        </button>
                    </div>
                )}

                <div
                    className="tv-chart-canvas-wrap"
                    style={Number(height) > 0 ? { height: Number(height), minHeight: Number(height) } : undefined}
                >
                    <div ref={wrapRef} className="tv-chart-canvas" />
                    {canDraw && (
                        <canvas
                            ref={overlayRef}
                            className="tv-chart-draw-overlay"
                            aria-hidden="true"
                        />
                    )}
                    {loading && (
                        <div className="tv-chart-state">
                            <Translate value="analysis_chart_loading" />
                        </div>
                    )}
                    {error && !loading && (
                        <div className="tv-chart-state tv-chart-state--error">
                            <Translate value="analysis_chart_error" />
                        </div>
                    )}
                    {!loading && !error && candles.length === 0 && (
                        <div className="tv-chart-state">
                            <Translate value="analysis_chart_empty" />
                        </div>
                    )}
                </div>
            </div>
            {canDraw && tool === TOOL_TREND && pendingTrend && (
                <p className="tv-chart-draw-hint">
                    <Translate value="chart_draw_trend_hint" />
                </p>
            )}
            {canDraw && tool !== TOOL_CURSOR && tool !== TOOL_TREND && (
                <p className="tv-chart-draw-hint">
                    <Translate value="chart_draw_click_hint" />
                </p>
            )}
        </div>
    );
};

TradingViewChart.propTypes = {
    symbol: PropTypes.string.isRequired,
    basePrice: PropTypes.number,
    entryPrice: PropTypes.number,
    candles: PropTypes.array,
    loading: PropTypes.bool,
    error: PropTypes.string,
    timeframe: PropTypes.string,
    onTimeframeChange: PropTypes.func,
    timeframes: PropTypes.array,
    height: PropTypes.number,
    drawingsEnabled: PropTypes.bool,
    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

TradingViewChart.defaultProps = {
    loading: false,
    error: '',
    height: 520,
    drawingsEnabled: false,
};

export default TradingViewChart;
